/**
 * Stale-chunk recovery — shared by lazyWithReload, main.tsx's `vite:preloadError`
 * listener, and the ErrorBoundary, so they all use ONE guard and can't fight.
 *
 * After a deploy the previous build's hashed JS chunks 404. An already-open tab that
 * lazy-loads a view (e.g. clicking the XP tab) then fails the dynamic import. The fix
 * is to reload once and pull the fresh bundle — but the reload must be coordinated
 * (one guard) and the failing view must HOLD rather than throw, or the branded error
 * page flashes for a moment before the reload lands.
 */

const RELOAD_KEY = 'chunkReloadAt';
/** How long after a reload we refuse to reload again — stops a genuinely-broken
 *  chunk from looping the page. */
const GUARD_MS = 10_000;

/**
 * Reload once to recover from a stale chunk. Guarded via sessionStorage so repeated
 * failures (or several handlers firing for the same failure) trigger at most one
 * reload per window. Returns true if a reload was actually triggered, false if the
 * guard suppressed it (a reload already happened very recently).
 */
export function reloadOnceForStaleChunk(): boolean {
  try {
    const last = Number(sessionStorage.getItem(RELOAD_KEY) || 0);
    if (Date.now() - last > GUARD_MS) {
      sessionStorage.setItem(RELOAD_KEY, String(Date.now()));
      window.location.reload();
      return true;
    }
  } catch {
    // sessionStorage can throw (privacy mode / disabled storage) — fall back to a
    // single unguarded reload attempt rather than getting stuck.
    window.location.reload();
    return true;
  }
  return false;
}

/**
 * Heuristic: does this error look like a failed dynamic import / stale chunk (as
 * opposed to a real render bug)? Matches the messages browsers and Vite produce when
 * a lazily-loaded module can't be fetched. Kept deliberately broad but message-based
 * so it won't swallow ordinary application errors.
 */
export function isChunkLoadError(err: unknown): boolean {
  const msg = err instanceof Error ? `${err.name}: ${err.message}` : String(err ?? '');
  return /loading chunk|chunkloaderror|dynamically imported module|importing a module script failed|failed to fetch dynamically imported|error loading dynamically imported|module script failed/i.test(msg);
}
